import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_STALE_TIME_MS } from "../constants";
import { fenceService } from "../services/fence-service";
import type { Fence, FenceFormData, FenceParams } from "../types/fence";

export function useFences(params?: FenceParams) {
	return useQuery({
		queryKey: ["fences", params],
		queryFn: () => fenceService.getFences(params),
		staleTime: QUERY_STALE_TIME_MS,
	});
}

export function useGetFenceById(id: number | null) {
	return useQuery<Fence>({
		queryKey: ["fence", id],
		queryFn: () => {
			if (id === null) {
				throw new Error("Fence id is required");
			}
			return fenceService.getFenceById(id);
		},
		enabled: !!id,
		staleTime: QUERY_STALE_TIME_MS,
	});
}

export function useCreateFence() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: FenceFormData) => fenceService.createFence(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fences"] });
		},
	});
}

export function useUpdateFence() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: FenceFormData }) =>
			fenceService.updateFence(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fences"] });
		},
	});
}

export function useDeleteFence() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: number) => fenceService.deleteFence(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fences"] });
		},
	});
}
